/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { forwardRef, Ref, useEffect, useState } from 'react';
import { CHECKBOX_RADIO_TYPE_PROMINENT } from '../fields/CheckboxRadio/CheckboxRadio.types';
import { FormControlOption } from './FormControl.defs';
import { FormControlProps, formControlPropsDefaults } from './FormControl.props';
import { FORM_CONTROL_TYPE_CHECKBOX, FORM_CONTROL_TYPE_RADIO } from './types/FormControlType.types';

/**
 * SDS Form Control component.
 */
export const FormControl = forwardRef((props: FormControlProps, ref: Ref<HTMLInputElement>): JSX.Element => {
  const {
    checkboxCallback,
    checkboxClick,
    checked,
    classes,
    customClick,
    customKeyDown,
    dataId,
    disabled,
    id,
    option,
    optionIndex,
    radioRef,
    readOnly,
    tabIndex,
    tabOverLabels,
    type,
    svg,
    variant,
  } = {
    ...formControlPropsDefaults,
    ...props,
  };

  // Used for Checkbox only
  const [isChecked, setChecked] = useState<boolean>(checked);

  // Aligns the states
  useEffect(() => {
    setChecked(checked);
  }, [checked]);

  const onOptionClick = (option: FormControlOption): void => {
    // For a checkbox, state is stored at the individual control level
    // For a radio button, state is stored at the parent list level
    if (customClick) {
      customClick(optionIndex);
    }
    if (type === FORM_CONTROL_TYPE_CHECKBOX) {
      setChecked(!isChecked);
      checkboxCallback && checkboxCallback(!isChecked);

      option.checked = !isChecked;
      checkboxClick && checkboxClick(option);
    }
  };
  const onOptionKeydown = (event: React.KeyboardEvent): void => {
    customKeyDown && customKeyDown(event);
  };

  const formControlBody = (
    <>
      {variant === CHECKBOX_RADIO_TYPE_PROMINENT && option.heading && (
        <span className="sds-form-control-heading">{option.heading}</span>
      )}
      <span className="sds-form-control-text">
        {svg} {option.label}
      </span>
    </>
  );

  const ariaLabel = `Option for ${option.label}`;
  let controlOptionIsChecked = false;

  if (type === FORM_CONTROL_TYPE_CHECKBOX && isChecked) {
    controlOptionIsChecked = true;
  } else if (type === FORM_CONTROL_TYPE_RADIO && checked) {
    controlOptionIsChecked = true;
  }

  const buttonClass = clsx(`sds-${type}-option__button`, {
    [`checked`]: controlOptionIsChecked,
    [`disabled`]: option.disabled,
  });

  if (disabled || readOnly || option.disabled) {
    return (
      <>
        <div className={classes}>
          <span
            aria-checked={controlOptionIsChecked}
            aria-label={ariaLabel}
            className={buttonClass}
            data-id={dataId}
            role={type}
          ></span>
          <div className="sds-form-control-label">{formControlBody}</div>
        </div>
        {option.extraContent}
      </>
    );
  }
  return (
    <>
      <div className={classes}>
        <button
          aria-checked={controlOptionIsChecked}
          aria-label={ariaLabel}
          className={buttonClass}
          data-checked={isChecked}
          data-for={id}
          data-id={dataId}
          data-value={option.value}
          disabled={option.disabled}
          onClick={(event): void => {
            event.preventDefault();
            onOptionClick(option);
          }}
          onKeyDown={(event): void => onOptionKeydown(event)}
          ref={radioRef}
          role={type}
          tabIndex={tabOverLabels ? -1 : tabIndex}
          type="button"
        ></button>
        <button
          className="sds-form-control-label"
          onClick={(event): void => {
            event.preventDefault();
            onOptionClick(option);
          }}
          tabIndex={tabOverLabels ? 0 : -1}
          type="button"
        >
          {formControlBody}
        </button>
      </div>
      {option.extraContent}
    </>
  );
});
